import { action, makeObservable, observable } from "mobx";
import { createContext } from "react";
import { ColumnDefinition } from "../components/base";
import api, {
  IDataRequest,
  IDataResponse,
  IHttpClient,
  STATUS_CODE,
} from "../providers/api";
import { notifierStore } from "./NotifierStore";
import FileDownload from "js-file-download";
import { FilterUtil } from "../commons/FilterUtil";

const PAGE_SIZE: number = 50;

export enum SUFIXO_FIELD {
  LIKE = "_like",
  NOT_LIKE = "_not_like",
  MORE = "_more",
  MORE_EQUAL = "_more_equal",
  LESS = "_less",
  LESS_EQUAL = "_less_equal",
  IS_NULL = "_is_null",
  IS_NOT_NULL = "_is_not_null",
  IN = "_in",
  NOT_IN = "_not_in",
  NOT_EQUAL = "_not_equal",
}

export enum CHECK_RULE {
  PAGE = "page",
  ALL = "all",
}

export interface OrFieldSearch {
  field: string;
  criteria: "" | SUFIXO_FIELD.LIKE;
  isNumber?: boolean;
  isDefaultSearch?: boolean;
  label?: string;
}

export interface Filter {
  field: string;
  value: any;
  criteria?: SUFIXO_FIELD;
  isDefaultSearch?: boolean;
  orField?: OrFieldSearch[];
  isNumber?: boolean;
  label?: string;
}

export interface FieldFilter {
  name: string;
  index: number;
  criteriaList: CriteriaListItem[]
}

export interface CriteriaListItem {
  text: string;
  value: "" | SUFIXO_FIELD
}

export const EQUAL_CRITERIA_LIST_ITEM: CriteriaListItem = {
  text: "=",
  value: ""
}

export const EQUAL_CRITERIA_LIST_ITEM_ALTERNATIVE: CriteriaListItem = {
  text: "igual",
  value: ""
}

export const MORE_CRITERIA_LIST_ITEM: CriteriaListItem = {
  text: ">",
  value: SUFIXO_FIELD.MORE
}

export const MORE_CRITERIA_LIST_ITEM_ALTERNATIVE: CriteriaListItem = {
  text: "maior",
  value: SUFIXO_FIELD.MORE
}

export const MORE_EQUAL_CRITERIA_LIST_ITEM: CriteriaListItem = {
  text: ">=",
  value: SUFIXO_FIELD.MORE_EQUAL
}

export const MORE_EQUAL_CRITERIA_LIST_ITEM_ALTERNATIVE: CriteriaListItem = {
  text: "maior igual",
  value: SUFIXO_FIELD.MORE_EQUAL
}

export const LESS_CRITERIA_LIST_ITEM: CriteriaListItem = {
  text: "<",
  value: SUFIXO_FIELD.LESS
}

export const LESS_CRITERIA_LIST_ITEM_ALTERNATIVE: CriteriaListItem = {
  text: "menor",
  value: SUFIXO_FIELD.MORE_EQUAL
}

export const LESS_EQUAL_CRITERIA_LIST_ITEM: CriteriaListItem = {
  text: "<=",
  value: SUFIXO_FIELD.LESS_EQUAL
}

export const LESS_EQUAL_CRITERIA_LIST_ITEM_ALTERNATIVE: CriteriaListItem = {
  text: "menor igual",
  value: SUFIXO_FIELD.LESS_EQUAL
}


export interface ResourcePaths {
  create: string;
  read: string;
  update: string;
  delete: string;
  list: string;
  cache?: string;
}


class DataGridStore {
  // Base Control
  paths: ResourcePaths = {
    create: "",
    read: "",
    update: "",
    delete: "",
    list: "",
  };
  columnDefinitions: ColumnDefinition[] = [];
  includes: string[] = [];
  expand: boolean = false;

  // Page Control
  pagination: boolean = true;
  page: number = 1;
  pageSize: number = PAGE_SIZE;
  totalRows: number = 0;
  orderBy: string[] = [];
  rows: any = [];
  aggregations: any = {};
  rowsPerPageOptions: number[] = [5, 10, 25, 50];

  // Filter Control
  filter: Filter[] = [];
  plainFilter: string = "";
  inputSearchValue: string = "";

  // Export Control
  fileNameExport: string = "";
  enableExport: boolean = false;


  // Check Control
  checkedAll: boolean = false;
  checkedList: string[] = [];
  checkRule: CHECK_RULE = CHECK_RULE.PAGE;


  constructor(private readonly provider: IHttpClient) {
    // this.plainFilter = "";
    makeObservable(this, {
      // Base control
      paths: observable,
      setPath: action,
      columnDefinitions: observable,
      setColumnDefinitions: action,
      includes: observable,
      setIncludes: action,
      addIncludes: action,
      removeIncludes: action,
      expand: observable,
      setExpand: action,

      // Page Control
      pagination: observable,
      page: observable,
      pageSize: observable,
      totalRows: observable,
      rows: observable,
      aggregations: observable,
      rowsPerPageOptions: observable,
      setPage: action,
      setPageSize: action,
      setTotalRows: action,
      setRows: action,
      setAggregations: action,
      setRowsPerPageOptions: action,
      orderBy: observable,
      setOrderBy: action,
      addOrderBy: action,
      removeOrderBy: action,

      // Filter Control
      filter: observable,
      setFilter: action,

      plainFilter: observable,
      setPlainFilter: action,
      getFilterState: action,

      inputSearchValue: observable,
      setInputSearchValue: action,

      // Export Control
      fileNameExport: observable,
      enableExport: observable,

      // Check Control
      checkedAll: observable,
      checkedList: observable,
      checkRule: observable,
      setCheckedList: action,
    });
  }

  // Base Control
  setPath(path: string | ResourcePaths): void {

    if (typeof path === "string") {
      this.paths = {
        create: path,
        read: path,
        update: path,
        delete: path,
        list: path,
      }
    } else {
      this.paths = { ...path }
    }

  }

  setPaths(paths: ResourcePaths): void {
    this.paths = paths
  }

  setColumnDefinitions(columnDefinitions: ColumnDefinition[]): void {
    this.columnDefinitions = columnDefinitions;
  }

  setIncludes(includes: string[]): void {
    this.includes = includes;
  }

  addIncludes(include: string): void {
    const includes = [...this.includes];
    if (includes.indexOf(include) > -1) {
      this.includes.push(include);
    }
  }

  removeIncludes(include: string): void {
    const idx = this.includes.indexOf(include);
    if (idx > -1) {
      const includes = [...this.includes];
      includes.splice(idx, 1);
    }
  }

  setExpand(expand: boolean): void {
    this.expand = expand;
  }

  // Page Control
  setPage(page: number): void {
    this.page = page;
  }

  setPageSize(pageSize: number): void {
    this.pageSize = pageSize;
  }

  setTotalRows(totalRows: number): void {
    this.totalRows = totalRows;
  }

  setRows(rows: any): void {
    this.checkedList = [];
    this.rows = rows;
  }

  setRowsPerPageOptions(rowsPerPageOptions: number[]): void {
    this.rowsPerPageOptions = rowsPerPageOptions;
  }

  setAggregations(aggregations: any): void {
    this.aggregations = aggregations
  }

  setOrderBy(orderBy: string[]): void {
    this.orderBy = orderBy;
  }

  addOrderBy(orderBy: string): void {
    this.orderBy.push(orderBy);
  }

  removeOrderBy(orderBy: string): void {
    const idx = this.orderBy.indexOf(orderBy);
    if (idx > -1) {
      this.orderBy.splice(idx, 1);
    }
  }

  get plainOrderBy(): string {
    return this.orderBy.length > 0 ? `&orderby=${this.orderBy.join(",")}` : "";
  }

  // Filter Control
  setFilter(filter: Filter[]): void {
    this.filter = filter;
    // this.setPlainFilter(this._buildFilter(this.filter));
  }

  setPlainFilter(plainFilter: string): void {
    this.plainFilter = plainFilter;
    this.setPage(1);
  }

  getFilterState(field: string, defaultValue: any): any {
    const idx = this.filter.findIndex((f) => {
      return f.field === field;
    });

    if (idx > -1) {
      return this.filter[idx].value;
    }
    return defaultValue;
  }

  get defaultFilterLabels(): string {
    const idx = this.filter.findIndex((f) => {
      return f.isDefaultSearch === true;
    });

    if (idx > -1) {
      const labels: string[] = [];

      if (this.filter[idx]?.label) {
        labels.push(this.filter[idx]?.label || "");
      }

      for (let orField of this.filter[idx]?.orField || []) {
        if (orField.label) {
          labels.push(orField.label);
        }
      }

      if (labels.length > 0) {
        return labels.join(", ");
      }

      return "";
    }

    return "";
  }

  _buildFilter(filterList: Filter[]): string {
    return FilterUtil.build(filterList)
  }

  setInputSearchValue(inputSearchValue: string): void {
    this.inputSearchValue = inputSearchValue;
  }

  // CRUD control
  async list(): Promise<void> {
    try {
      const urlOthers: string[] = [];

      // Paginação
      if (this.pagination) {
        urlOthers.push(`&page=${this.page}&limit=${this.pageSize}`);
      }

      // Includes
      if (this.includes.length) {
        urlOthers.push(`&includes=${this.includes.join(",")}`);
      }

      // Filter
      const filter = this._buildFilter(this.filter);
      if (filter) {
        urlOthers.push(filter);
      }

      // Ordenação
      const orderBy = this.plainOrderBy;
      if (orderBy) {
        urlOthers.push(this.plainOrderBy);
      }

      const requestConfig: IDataRequest = {
        url: `${this.paths.list}/${urlOthers.length ? "?" + urlOthers.join("").substring(1) : ""}`,
        headers: {
          noloading: true,
        },
      };
      const response: IDataResponse = await this.provider.get(requestConfig);

      if (response.statusCode === STATUS_CODE.OK) {
        this.setRows([]);

        if (this.pagination) {
          this.setRows(response.data?.rows);
          this.setAggregations(response?.data?.aggregates || {})
          this.setTotalRows(response.data?.infoRows?.count);
        } else {
          this.setRows(response.data);
          this.setAggregations({});
          this.setTotalRows(0);
        }
      }

      if (response.statusCode !== STATUS_CODE.OK) {
        this.setRows([]);
        this.setRows(0);
        this.setAggregations({})
        this.setTotalRows(0);
      }

    } catch (error) {
      this.setRows([]);
      this.setRows(0);
      this.setAggregations({})
      this.setTotalRows(0);
    }
  }

  async save({
    row,
    primaryKey = "",
  }: {
    row: any;
    primaryKey?: number | string;
  }): Promise<IDataResponse> {
    try {
      const isUpdate: boolean = primaryKey !== "" ? true : false;
      const url: string = isUpdate ? `${this.paths.update}/${primaryKey}` : `${this.paths.create}`;

      const requestConfig: IDataRequest = {
        url: `${url}`,
        data: row,
      };

      let response: IDataResponse = isUpdate
        ? await this.provider.put(requestConfig)
        : await this.provider.post(requestConfig);

      if ([STATUS_CODE.OK, STATUS_CODE.CREATED].includes(response.statusCode)) {
        notifierStore.setNotifier({
          content: "Registro salvo com sucesso!!",
          title: "Aviso",
          timeOut: 2500,
          onClose: () => notifierStore.setOpen(false),
        });

        notifierStore.setOpen(true);
        return response;
      }
      return {
        data: null,
        statusCode: response.statusCode,
        message: response.message,
      };
    } catch (error) {
      throw error;
    }
  }

  async find(
    primaryKey: string | number,
    includes: string[] = []
  ): Promise<any> {
    const _includes =
      includes.length > 0 ? `/?includes=${includes.join(",")}` : "";

    const requestConfig: IDataRequest = {
      url: `${this.paths.read}/${primaryKey}${_includes}`,
    };

    const response: IDataResponse = await this.provider.get(requestConfig);

    if (response.statusCode === STATUS_CODE.OK) {
      return response.data.length > 0 ? response.data[0] : null;
    }

    return null;
  }

  async delete(primaryKey: number): Promise<boolean> {
    let deleted = false;

    const requestConfig: IDataRequest = {
      url: `${this.paths.delete}/${primaryKey}`,
    };

    const response: IDataResponse = await this.provider.delete(requestConfig);

    if (response.statusCode === STATUS_CODE.OK) {
      notifierStore.setNotifier({
        content: "Registro removido com sucesso!!",
        title: "Aviso",
        timeOut: 2500,
        onClose: () => notifierStore.setOpen(false),
      });
      notifierStore.setOpen(true);
      deleted = true;
    }

    return deleted;
  }

  setPagination(enablePagination: boolean): void {
    this.pagination = enablePagination;
  }

  // Export grid control
  setEnableExport(enableExport: boolean): void {
    this.enableExport = enableExport;
  }

  async export(): Promise<void> {
    const _includes =
      this.includes.length > 0 ? `&includes=${this.includes.join(",")}` : "";
    const _filter = this._buildFilter(this.filter);
    const response = await this?.provider?.getFile({
      url: `${this.paths.list}?format=xls${_filter}${_includes}${this.plainOrderBy}`,
    });

    if (response?.statusCode === STATUS_CODE.OK && response?.data) {
      FileDownload(
        response.data,
        this.fileNameExport,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
    }
  }

  toggleCheckAll(field: string): void {
    if (this.checkedAll) {
      this.checkedList = [];
      this.checkedAll = false;
      return;
    }

    const checkeds = [];

    const _row = [...this.rows].shift();

    const keys = Object.keys(_row);

    if (!keys.includes(field)) {
      console.error(`o campo "${field}" não está presente nos campos definidos desse objeto (${keys.join(", ")})`)
      return;
    }

    for (let r of this.rows) {
      checkeds.push(r[field])
    }
    this.checkedAll = true;
    this.checkedList = checkeds;
    return;
  }

  rowIsChecked(row: any, field: string): boolean {
    return this.checkedList.includes(row[field])
  }

  toggleRowCheck(row: any, field: string): void {
    const _checkedList = [...this.checkedList];
    const idxRow = _checkedList.findIndex((f) => {
      return String(f) === String(row[field])
    })

    if (idxRow > -1) {
      _checkedList.splice(idxRow, 1);
    } else {
      _checkedList.push(row[field]);
    }

    this.checkedList = _checkedList;
  }

  setCheckedList(checkedList: string[]): void {
    this.checkedList = checkedList;
  }

  isIndeterminate(): boolean {
    return this.rows.length > 0 && this.checkedList.length > 0 && this.rows.length !== this.checkedList.length
  }
}

export default createContext(new DataGridStore(api));

export { DataGridStore };
